/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React from "react";
import {
  Button,
  DescriptionIcon,
  ImportIcon,
  ProgressIndicator
} from "@forge/common";
import { useTranslation } from "react-i18next";
import { LOCALE_CONST } from "../../../utils/constants";
import { convertUTCStringtoFormattedDate } from "../../../utils/utility";
import "./FileDownload.scss";

/**
 * FileDownload component props interface.
 *
 * @interface FileDownloadProps
 * @property {string} fileUrl - The URL of the file to be downloaded.
 * @property {string} fileName - The name of the file to be downloaded.
 * @property {string} uploadedTime - The time when the file was uploaded.
 * @property {string} gatewayStatus - The status of the gateway, like "In Progress" or "Success".
 */

/**
 * FileDownload component.
 *
 * This component displays a file download option with its details such as file name,
 * uploaded time, and gateway status. If the gateway status is "In Progress",
 * it shows a progress indicator.
 * If the gateway status is "Success", it shows a download button.
 *
 * @component
 * @param {FileDownloadProps} props - The props for the FileDownload component.
 * @returns {JSX.Element} The rendered FileDownload component.
 */
export interface FileProps {
  fileSubType?: string;
  fileName: string;
  uploadedTime: string;
  availableToDownload: boolean;
}

export interface FileDownloadProps {
  file: FileProps;
  onDownloadClick: (file: FileProps) => void;
  isInProgress: boolean;
}

const FileDownload: React.FC<FileDownloadProps> = ({
  file,
  onDownloadClick,
  isInProgress
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex diagnostics-file-download-container">
      <div className="icon-file-wrapper flex justify-center items-center">
        <DescriptionIcon fontSize="24" />
      </div>
      <div className="flex flex-col w-full">
        <span className="font-size-14">{file?.fileName}</span>
        <span className="font-size-14">
          {convertUTCStringtoFormattedDate(file?.uploadedTime || "")}
        </span>
        {/* Rendering Progress indication incase of gatewayStatus is In Progress */}
        {isInProgress && (
          <div
            className="w-full progress-container"
            data-testid="progress-indicator"
          >
            {/* @TODO: Replace fileSize, uploaded with actual api resp */}
            <ProgressIndicator
              fileName={""}
              fileSize={200}
              uploaded={100}
            ></ProgressIndicator>
          </div>
        )}
      </div>

      {/* Downlaod option available only if gatewayStatus is Success */}
      {!isInProgress && (
        <Button
          variant="quiet-secondary"
          onClick={() => {
            onDownloadClick(file);
          }}
          className="ml-auto"
          icon={<ImportIcon />}
          disabled={!file?.availableToDownload}
        >
          {t(LOCALE_CONST.reportDownload)}
        </Button>
      )}
    </div>
  );
};

export default FileDownload;
